import { ApolloError } from '@apollo/client';
import { INVITE_CODE_QUERY_PARAM } from '@modules/users/constants';
import {
  GetUserByEmailDocument,
  useProcessOperationMutation,
} from 'generated/graphql';
import { useCallback, useEffect, useRef } from 'react';
import AuthService from 'services/AuthService';
import { createAnalyticsTracker } from 'v2/analytics/events';

const trackForceLogoutEvents = createAnalyticsTracker('forceLogout');

interface ProcessOperationForAuthOptions {
  shouldRefetchUserOnSuccess?: boolean;
  onCompleted?: () => void;
  onError?: (error: ApolloError) => void;
}

export const useProcessInviteCodeForAuth = (
  options?: ProcessOperationForAuthOptions,
) => {
  // (aryanshridhar) use of useRef might not be required here, added for safety
  // so that the inviteCode is not lost when this hook is re-triggered.
  const inviteCodeRef = useRef<null | string>(null);
  const [processOperation, { loading, error }] = useProcessOperationMutation({
    onCompleted: options?.onCompleted,
    onError: (error) => {
      AuthService.setIsInviteCodeVerificationFailed();
      trackForceLogoutEvents({
        eventName: 'Forced User Logout',
        fromURL: window.location.href,
        operationName: 'processOperation',
      });
      window.location.href = '/logout';

      options?.onError?.(error);
    },
  });

  const search = new URLSearchParams(window.location.search);
  const inviteCode = search.get(INVITE_CODE_QUERY_PARAM);

  useEffect(() => {
    if (inviteCode) {
      inviteCodeRef.current = inviteCode;
    }
  }, [inviteCode]);

  const processInviteCodeIfPresent = useCallback(async () => {
    if (!inviteCodeRef.current) {
      return;
    }

    await processOperation({
      variables: {
        request: {
          operationToken: inviteCodeRef.current,
        },
      },
      refetchQueries: options?.shouldRefetchUserOnSuccess
        ? [GetUserByEmailDocument]
        : undefined,
      awaitRefetchQueries: true,
    });
  }, [options?.shouldRefetchUserOnSuccess, processOperation]);

  return {
    processInviteCodeIfPresent,
    isLoading: loading,
    error,
  };
};
